import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useAddRootstockMutation } from 'common/api/endpoints/variety';
import { handleError } from 'common/api/utils/handleError';
import FormPrompt from 'common/components/FormPrompt';
import * as notifier from 'common/services/notification';
import { orange } from 'common/styles/colors';
import { FC, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { addRootstockSchema } from 'utils/schemas/settings';
import { AddSettingBtn, AddSettingForm } from '../../styles';
import { RootstockCodeInput } from './styles';

type AddRootstock = {
  code: string | null;
};

export const AddRootstock: FC = () => {
  const [showForm, setShowForm] = useState(false);

  const [addRootstock, { isLoading: isSubmitting }] = useAddRootstockMutation();

  const methods = useForm<AddRootstock>({
    defaultValues: {
      code: null,
    },
    resolver: yupResolver(addRootstockSchema),
  });
  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
  } = methods;

  const closeForm = () => {
    reset();
    setShowForm(false);
  };

  const onAddSubmit = async (data: AddRootstock): Promise<void> => {
    if (!isDirty) {
      // No values to save.
      closeForm();
      return;
    }

    try {
      await addRootstock({ code: data.code as string }).unwrap();
      notifier.showSuccessMessage('Rootstock added.');
      closeForm();
    } catch (error) {
      handleError(error, 'Unable to add rootstock.');
    }
  };

  return showForm ? (
    <FormProvider<AddRootstock> {...methods}>
      <AddSettingForm onSubmit={handleSubmit(onAddSubmit)}>
        <div>
          <RootstockCodeInput
            {...register('code')}
            placeholder='Code...'
            $isError={!!errors.code}
            onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />
          <Form.Check.Input hidden isInvalid={!!errors.code} />
          {errors.code && (
            <Form.Control.Feedback type='invalid'>
              {errors.code.message}
            </Form.Control.Feedback>
          )}
        </div>
        <button type='submit' className='add-btn' disabled={isSubmitting}>
          Add Rootstock
        </button>
        <button
          type='button'
          className='cancel-btn'
          onClick={closeForm}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </AddSettingForm>
      <FormPrompt isDirty={isDirty} isSubmitting={isSubmitting} />
    </FormProvider>
  ) : (
    <AddSettingBtn type='button' onClick={() => setShowForm(true)}>
      <AddCircleOutlineOutlinedIcon htmlColor={orange} />
      Add New Rootstock
    </AddSettingBtn>
  );
};
